import { ArrowLeft } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { douyinStatisticsSearch, useDouyinStatisticsPeriod } from '../douyinStatistics'
import { douyinPath, useDouyinStoreId } from '../douyinStore'
import type { DouyinLiveAnalysis, DouyinSessionDetail } from '../douyinTypes'
import { integer, money } from '../utils'
import { DouyinBoundary, DouyinProductImage, DySigned, DyTag, dyDuration, dyInt, dyPct1, dyPct2, dyYuan, dyYuan0 } from './douyinCommon'

export function DouyinLiveSessionDetailPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>()
  return <DouyinBoundary>{(data) => <DouyinLiveSessionDetailContent data={data} sessionId={sessionId} />}</DouyinBoundary>
}

function DouyinLiveSessionDetailContent({ data, sessionId }: { data: DouyinLiveAnalysis; sessionId: string }) {
  const storeId = useDouyinStoreId()
  const [period] = useDouyinStatisticsPeriod('day')
  const statisticsSearch = douyinStatisticsSearch(period)
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(() => new Set())
  const session = data.sessions.find((item) => item.k === sessionId)
  const detail: DouyinSessionDetail | undefined = data.sessionDetails[sessionId]
  const products = useMemo(() => (detail?.products || [])
    .filter((product) => (product.paid || 0) > 0 || (product.items || 0) > 0)
    .slice()
    .sort((left, right) => (right.paid || 0) - (left.paid || 0)), [detail])

  if (!session) {
    return <div className="not-found"><h1>未找到该直播场次</h1><p>场次 {sessionId || '未提供'}</p><Link to={`${douyinPath(storeId, '/live')}${statisticsSearch}`}>返回直播场次</Link></div>
  }
  const title = `${session.date} ${session.start} 场`
  const roi = session.ad ? (session.paid / session.ad).toFixed(1) : '—'
  const maxPaid = Math.max(...products.map((product) => product.paid || 0), 1)
  const toggleProduct = (index: number) => setExpandedProducts((current) => {
    const next = new Set(current)
    if (next.has(index)) next.delete(index)
    else next.add(index)
    return next
  })

  const formula: Array<{ label: string; value: number | null } & { cls?: 'hl' | 'res' } | { op: string }> = [
    { label: '支付金额', value: session.paid, cls: 'hl' }, { op: '−' },
    { label: '产品成本', value: session.cost }, { op: '−' },
    { label: '扣点5%', value: session.fee }, { op: '−' },
    { label: '税点5%', value: session.tax }, { op: '−' },
    { label: '运费5元/单', value: session.ship }, { op: '−' },
    { label: '运费险1.5元/单', value: session.insure }, { op: '−' },
    { label: '达人佣金', value: session.commission }, { op: '=' },
    { label: '估算毛利', value: session.gross, cls: 'res' }, { op: '−' },
    { label: '千川消耗', value: session.ad }, { op: '=' },
    { label: '广告后利润', value: session.afterAd, cls: 'res' },
  ]
  const rebateFormula: Array<{ label: string; value: number | null } & { cls?: 'hl' | 'res' } | { op: string }> = [
    { label: '估算毛利', value: session.gross }, { op: '＋' },
    { label: '返点金额', value: session.rebate, cls: 'hl' }, { op: '=' },
    { label: '含返点毛利', value: session.grossWithRebate, cls: 'res' }, { op: '−' },
    { label: '千川消耗', value: session.ad }, { op: '=' },
    { label: '含返点广告后利润', value: session.afterAdWithRebate, cls: 'res' },
  ]

  return <div className="page-stack dy-page dy-live-detail-page">
    <div className="breadcrumb-row"><Link to={`${douyinPath(storeId, '/live')}${statisticsSearch}`}><ArrowLeft aria-hidden="true" />返回</Link><span>直播场次</span><span>/</span><span>{title}</span></div>
    <MetricStrip items={[
      { label: '用户支付金额', value: money(session.paid), context: `${integer(session.items)} 件 / ${integer(session.orders)} 单 · 时长 ${dyDuration(session.dur)}` },
      { label: '千川消耗', value: money(session.ad), context: `投产比 ${roi}` },
      { label: '估算毛利', value: session.gross == null ? '—' : money(session.gross), context: session.gross == null ? '成本口径待确认' : `未含返点 · 毛利率 ${session.paid ? dyPct1(session.gross / session.paid) : '—'}` },
      { label: '返点金额', value: session.rebate == null ? '—' : money(session.rebate), context: session.rebate == null ? '返点规则待确认' : '普通款成本20%' },
      { label: '含返点毛利', value: session.grossWithRebate == null ? '—' : money(session.grossWithRebate), context: session.grossWithRebate == null ? '返点规则待确认' : `毛利率 ${session.paid ? dyPct1(session.grossWithRebate / session.paid) : '—'}` },
      { label: '含返点广告后利润', value: session.afterAdWithRebate == null ? '—' : money(session.afterAdWithRebate), context: session.afterAdWithRebate == null ? '成本与返点口径待确认' : '含返点毛利 − 千川' },
      { label: '退款金额', value: money(session.refund), context: '不进利润，单列' },
      { label: '客单价', value: dyYuan(detail?.aov), context: `GPM ${dyYuan(detail?.gpm)}` },
    ]} />
    {data.meta.profitReady === false ? <section className="overview-section"><div className="section-heading-row"><div><h2>利润拆解</h2><p>成本口径待确认</p></div></div><div className="pdd-caliber-note">{data.meta.caliber}</div></section> : <section className="overview-section">
      <div className="section-heading-row"><div><h2>利润拆解</h2><p>估算口径 · 与场次列表一致</p></div></div>
      <p className="dy-formula-label">未含返点</p>
      <div className="dy-formula">{formula.map((item, index) => 'op' in item
        ? <span className="dy-fop" key={index}>{item.op}</span>
        : <div className={`dy-fitem ${item.cls || ''}`.trim()} key={index}>
          <div className={`dy-fv ${item.cls === 'res' && (item.value ?? 0) < 0 ? 'is-neg' : ''}`.trim()}>{dyYuan(item.value)}</div>
          <div className="dy-fl">{item.label}</div>
        </div>)}</div>
      <p className="dy-formula-label">计入返点</p>
      <div className="dy-formula">{rebateFormula.map((item, index) => 'op' in item
        ? <span className="dy-fop" key={index}>{item.op}</span>
        : <div className={`dy-fitem ${item.cls || ''}`.trim()} key={index}>
          <div className={`dy-fv ${item.cls === 'res' && (item.value ?? 0) < 0 ? 'is-neg' : ''}`.trim()}>{dyYuan(item.value)}</div>
          <div className="dy-fl">{item.label}</div>
        </div>)}</div>
      <p className="dy-legend">返点：普通非配件、非品牌特价 SKU 按产品成本20%计算，配件与品牌特价不计；未含包装费、赠品费（成本档案未确认）；本场缺成本金额 {money(session.nocost)}（SKU 未接编码部分，不计 0）。</p>
    </section>}
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>这场卖了什么</h2><p>{integer(products.length)} 个 SPU · 按支付金额降序 · 点击行展开 SKU</p></div></div>
      <div className="table-responsive"><table className="product-table dy-table dy-live-prod-table"><thead><tr>
        <th className="l dy-col-prod">商品</th><th>支付金额</th><th className="l">占比</th><th>成交件数</th><th>成交订单</th><th>产品成本</th><th>货差毛利</th><th>返点金额</th><th>含返点货差</th><th>退款金额</th>
      </tr></thead><tbody>{products.map((product, index) => {
        const info = data.spuInfo[product.pid]
        const grossMargin = product.cost != null ? product.paid - product.cost : null
        const grossMarginWithRebate = data.meta.profitReady === false ? null : grossMargin != null ? grossMargin + (product.rebate || 0) : null
        const hasEligibleRebate = (product.skus || []).some((sku) => sku.rebateRate != null)
        const expanded = expandedProducts.has(index)
        return <SessionProductRows key={`${product.pid}-${index}`} expanded={expanded} onToggle={() => toggleProduct(index)}>
          <tr className="dy-clickable-row" onClick={() => toggleProduct(index)}>
            <td className="l dy-col-prod">
              <div className="product-link">
                <DouyinProductImage src={info?.img || null} />
                <span className="dy-prod-main">
                  <strong title={product.n}>{product.n}</strong>
                  <small>
                    <span>SPU · {product.pid}</span>
                    {product.residual != null ? <DyTag kind="warn">SPU残差 {dyYuan0(product.residual)}</DyTag> : null}
                  </small>
                </span>
              </div>
            </td>
            <td><b>{money(product.paid)}</b></td>
            <td className="l"><span className="dy-bar"><i style={{ width: `${((product.paid || 0) / maxPaid * 100).toFixed(1)}%` }} /></span></td>
            <td>{integer(product.items)}</td>
            <td>{integer(product.orders)}</td>
            <td>{product.cost != null ? money(product.cost) : '—'}</td>
            <td>{grossMargin == null ? '—' : <DySigned value={grossMargin} />}</td>
            <td>{product.rebate == null ? '—' : hasEligibleRebate ? money(product.rebate) : '不计'}</td>
            <td>{grossMarginWithRebate == null ? '—' : <DySigned value={grossMarginWithRebate} />}</td>
            <td>{product.refund ? money(product.refund) : '—'}</td>
          </tr>
          {expanded ? (product.skus || []).map((sku, skuIndex) => {
            const skuGrossMargin = sku.cost != null ? sku.paid - sku.cost : null
            const skuGrossMarginWithRebate = data.meta.profitReady === false ? null : skuGrossMargin != null ? skuGrossMargin + (sku.rebate || 0) : null
            const rebateLabel = data.meta.profitReady === false ? '—' : sku.rebateRate != null
              ? `${money(sku.rebate || 0)}（${dyPct1(sku.rebateRate)}）`
              : sku.cost == null ? '—' : sku.accessory ? '配件不计' : sku.costMode === 'brand-special' ? '特价不计' : '不计'
            return <tr className="dy-sku-row" key={`${sku.n}-${skuIndex}`}>
              <td className="l">↳ {sku.n}</td>
              <td className="l">{sku.code ? <span className="dy-pill">{sku.code}</span> : <DyTag kind="uncoded">未接编码</DyTag>}</td>
              <td>{money(sku.paid)}</td>
              <td>{integer(sku.items)}</td>
              <td>{integer(sku.orders)}</td>
              <td>{sku.uc != null ? `${money(sku.uc)}/件` : '—'}</td>
              <td>{skuGrossMargin == null ? '—' : <DySigned value={skuGrossMargin} />}</td>
              <td>{rebateLabel}</td>
              <td>{skuGrossMarginWithRebate == null ? '—' : <DySigned value={skuGrossMarginWithRebate} />}</td>
              <td>{sku.refund ? money(sku.refund) : '—'}</td>
            </tr>
          }) : null}
        </SessionProductRows>
      })}</tbody></table></div>
    </section>
    {detail?.flow ? <SessionFlowSections detail={detail} /> : <div className="pdd-caliber-note">达人合作场的整场导出不含流量分析三表（平台差异，非数据缺失），漏斗、渠道与短视频引流不可得。</div>}
    {detail && detail.videos.length ? <SessionVideoSection detail={detail} /> : null}
  </div>
}

// tbody 内的行组容器：直接透传 children，仅为可读性分组。
function SessionProductRows({ children }: { children: ReactNode; expanded: boolean; onToggle: () => void }) {
  return <>{children}</>
}

function SessionFlowSections({ detail }: { detail: DouyinSessionDetail }) {
  const funnel = detail.funnel || {}
  const board = detail.board || {}
  const steps: Array<[string, number | null | undefined, number | null | undefined]> = [
    ['直播间曝光', funnel.exposure_users, null],
    ['进入直播间', funnel.entering_users, funnel.exposure_view_rate_users],
    ['商品曝光', funnel.product_exposure_users, null],
    ['商品点击', funnel.product_click_users, board.product_exposure_click_rate_users],
    ['成交', funnel.paid_users, board.product_click_pay_rate_users],
  ]
  const topValue = steps[0][1] || 1
  const channels = (detail.channels || []).slice().sort((left, right) => (right.paid || 0) - (left.paid || 0))
  const overall = channels.find((channel) => channel.n === '整体')
  const channelTotal = overall ? (overall.paid || 0) : channels.reduce((total, channel) => total + (channel.paid || 0), 0)
  return <div className="dy-grid2">
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>成交转化漏斗</h2><p>人数口径 · 次要区块</p></div></div>
      <div className="dy-funnel">{steps.map(([label, value, rate]) => <div className="dy-fstep" key={label}>
        <div className="dy-fname">{label}</div>
        <div className="dy-fbar"><i style={{ width: `${Math.max(2, (value || 0) / topValue * 100).toFixed(1)}%` }} /></div>
        <div className="dy-fval">{dyInt(value)} 人</div>
        <div className="dy-frate">{rate == null ? '' : dyPct2(rate)}</div>
      </div>)}</div>
      <p className="dy-legend">曝光/进入等人数在罗盘导出中被缩写为万位，转化率一律采信平台给出的值。</p>
    </section>
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>渠道分析</h2><p>哪个渠道给的量最值钱 · 次要区块</p></div></div>
      <div className="table-responsive"><table className="product-table dy-table dy-channel-table"><thead><tr>
        <th className="l">渠道</th><th>观看人数</th><th>观看次数</th><th>人均时长</th><th>支付金额</th><th>占比</th><th>订单</th><th>笔单价</th><th>观看-成交率</th>
      </tr></thead><tbody>{channels.map((channel) => <tr key={channel.n}>
        <td className="l">{channel.n}</td>
        <td>{dyInt(channel.vu)}</td>
        <td>{dyInt(channel.vt)}</td>
        <td>{channel.dur != null ? `${integer(channel.dur)} 秒` : '—'}</td>
        <td><b>{dyYuan(channel.paid)}</b></td>
        <td>{channelTotal ? `${((channel.paid || 0) / channelTotal * 100).toFixed(1)}%` : '—'}</td>
        <td>{dyInt(channel.orders)}</td>
        <td>{channel.po != null ? money(channel.po) : '—'}</td>
        <td>{dyPct2(channel.rate)}</td>
      </tr>)}</tbody></table></div>
      <p className="dy-legend">千川消耗仅「整体」行有值，分渠道投产比不可得。</p>
    </section>
  </div>
}

function SessionVideoSection({ detail }: { detail: DouyinSessionDetail }) {
  return <section className="overview-section">
    <div className="section-heading-row"><div><h2>短视频引流</h2><p>{integer(detail.videos.length)} 条投稿</p></div></div>
    <div className="table-responsive"><table className="product-table dy-table dy-video-table"><thead><tr>
      <th className="l">短视频标题</th><th>投稿时间</th><th>入口曝光次数</th><th>引流进直播间</th><th>入口点击率</th>
    </tr></thead><tbody>{detail.videos.map((video, index) => <tr key={`${video.at}-${index}`}>
      <td className="l"><span title={video.t}>{(video.t || '').slice(0, 34)}</span></td>
      <td>{video.at}</td>
      <td>{dyInt(video.exp)}</td>
      <td><b>{dyInt(video.in)}</b></td>
      <td>{dyPct2(video.rate)}</td>
    </tr>)}</tbody></table></div>
    <p className="dy-legend">罗盘不提供短视频ID，标题+投稿时间为弱标识，不做跨场累计。</p>
  </section>
}
